import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AdminMenuBar } from '../utils/admin-menu-bar';
import { PillTabs } from '..';
import { DateRangePicker } from '../dashboard';
import { AdminAnalytics, AdminDaily, AdminHourly, AdminWeekly } from '../../api/admin-analytics';

const CELL = { whiteSpace: 'nowrap', py: 1, px: 2, border: 1, borderColor: 'divider' };
const HEAD_CELL = { ...CELL, fontWeight: 600 };

const DAILY_TAB = { value: 'daily', label: 'Daily' };
const WEEKLY_TAB = { value: 'weekly', label: 'Weekly' };
// The hourly job (post expiry sweep) only exists for loise.
const HOURLY_TAB = { value: 'hourly', label: 'Hourly' };

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// Epoch ms → 'YYYY-MM-DD HH:mm' in KST (the cron schedule's timezone), i.e. when a job ran.
function fmtRunTime(ms) {
    if (!ms) return '—';
    return new Date(ms + KST_OFFSET_MS).toISOString().slice(0, 16).replace('T', ' ');
}

// `ran_at` is when the scheduler job wrote the row (blank for rows from before it was recorded).
const RUN_COL = { key: 'ran_at', label: 'Date', align: 'left', format: fmtRunTime };

// Daily. loise shows what the daily job handled (`daily_stats`); the KPI counts live in the dashboards.
// account's only daily job is the user-count rollup, so it shows that row (`date` = the day it counted).
const COLS = {
    account: [
        RUN_COL,
        { key: 'date', label: 'Target', align: 'left' },
        { key: 'total_users', label: 'Total Users' },
        { key: 'new_users', label: 'New Users' },
        { key: 'deleted_users', label: 'Deleted' },
        { key: 'login_count', label: 'Logins' },
    ],
    loise: [
        RUN_COL,
        { key: 'deleted_messages', label: 'Deleted Messages' },
        { key: 'synced_post_stats', label: 'Synced Post Stats' },
        { key: 'expired_subscriptions', label: 'Expired Subscriptions' },
        { key: 'deleted_rate_limits', label: 'Deleted Rate Limits' },
        { key: 'deleted_restrictions', label: 'Deleted Restrictions' },
    ],
};

const HOURLY_COLS = [
    RUN_COL,
    { key: 'expired_posts', label: 'Expired Posts' },
    { key: 'deleted_r2_objects', label: 'Deleted Images' },
    { key: 'deleted_comments', label: 'Deleted Comments' },
    { key: 'deleted_likes', label: 'Deleted Likes' },
];

const WEEKLY_COLS = [
    { key: 'week_start_at', label: 'Date', align: 'left', format: fmtRunTime },
    { key: 'processed_segments', label: 'Processed Segments' },
    { key: 'ranked_users', label: 'Ranked Users' },
];

// Newest-first table of per-period rows. `load(setData)` fills `{ loading, rows }`; it re-runs
// whenever `loadKey` changes (period / service).
function PeriodTab({ columns, load, loadKey, rowKey }) {
    const [data, setData] = useState({ loading: false, rows: [] });

    useEffect(() => {
        load(setData);
    }, [loadKey]);

    return (
        <Box sx={{ overflow: 'auto', flex: 1 }}>
            {data.loading ? (
                <Table size='small' sx={{ borderCollapse: 'collapse' }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell key={col.key} sx={HEAD_CELL}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.from({ length: 5 }, (_, i) => (
                            <TableRow key={i}>
                                {columns.map((col) => (
                                    <TableCell key={col.key} sx={CELL}>
                                        <Skeleton />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Table size='small' sx={{ borderCollapse: 'collapse' }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell key={col.key} sx={{ ...HEAD_CELL, textAlign: col.align ?? 'right' }}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ ...CELL, color: 'text.secondary' }}>
                                    No data
                                </TableCell>
                            </TableRow>
                        ) : (
                            [...data.rows].reverse().map((row) => (
                                <TableRow key={row[rowKey]} hover>
                                    {columns.map((col) => (
                                        <TableCell key={col.key} sx={{ ...CELL, textAlign: col.align ?? 'right' }}>
                                            {col.format && row[col.key] != null
                                                ? col.format(row[col.key])
                                                : (row[col.key] ?? '—')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}

function SchedulerStats({ service }) {
    const [selected, setTab] = useState(HOURLY_TAB.value);
    // Period in hours, shared picker with the dashboards (presets + calendar range).
    const [hours, setHours] = useState(168);
    const [dailyHours, setDailyHours] = useState(720);
    const [weeklyHours, setWeeklyHours] = useState(2160);
    const days = Math.max(1, Math.ceil(dailyHours / 24));
    const weeklyDays = Math.max(1, Math.ceil(weeklyHours / 24));
    const [hourlyKey, setHourlyKey] = useState(0);
    const [dailyKey, setDailyKey] = useState(0);
    const [weeklyKey, setWeeklyKey] = useState(0);

    const tabs = service === 'loise' ? [HOURLY_TAB, DAILY_TAB, WEEKLY_TAB] : [DAILY_TAB, WEEKLY_TAB];
    // Hourly is the default; services without it (account) and instances reused across services
    // fall back to their first tab.
    const tab = tabs.some((t) => t.value === selected) ? selected : tabs[0].value;

    const hourlyApiItems = [{ label: 'Refresh', onClick: () => setHourlyKey((k) => k + 1) }];
    const dailyApiItems = [{ label: 'Refresh', onClick: () => setDailyKey((k) => k + 1) }];
    const weeklyApiItems = [{ label: 'Refresh', onClick: () => setWeeklyKey((k) => k + 1) }];
    const apiItems = { hourly: hourlyApiItems, daily: dailyApiItems, weekly: weeklyApiItems }[tab];
    const reload = apiItems[0].onClick;

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <PillTabs
                value={tab}
                onChange={setTab}
                tabs={tabs}
                right={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size='small' onClick={reload}>
                            <RefreshIcon fontSize='small' />
                        </IconButton>
                        {tab === 'hourly' && <DateRangePicker hours={hours} onChange={setHours} />}
                        {tab === 'daily' && <DateRangePicker hours={dailyHours} onChange={setDailyHours} />}
                        {tab === 'weekly' && <DateRangePicker hours={weeklyHours} onChange={setWeeklyHours} />}
                    </Box>
                }
            />
            <AdminMenuBar apiItems={apiItems} />
            {tab === 'hourly' && (
                <PeriodTab
                    key={hourlyKey}
                    columns={HOURLY_COLS}
                    load={(setData) => AdminHourly(setData, Math.ceil(hours))}
                    loadKey={hours}
                    rowKey='hour'
                />
            )}
            {tab === 'daily' && (
                <PeriodTab
                    key={dailyKey}
                    columns={COLS[service]}
                    load={(setData) =>
                        service === 'loise' ? AdminDaily(setData, days) : AdminAnalytics(setData, days, service)
                    }
                    loadKey={`${days}:${service}`}
                    rowKey='date'
                />
            )}
            {tab === 'weekly' && (
                <PeriodTab
                    key={weeklyKey}
                    columns={WEEKLY_COLS}
                    load={(setData) => AdminWeekly(setData, weeklyDays)}
                    loadKey={weeklyDays}
                    rowKey='week_start_at'
                />
            )}
        </Stack>
    );
}

export { SchedulerStats };
